import { AlertTriangle, ExternalLink, KeyRound, Mail, Plus, RefreshCw, SearchX } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { AssignRolesDrawer } from "@/modules/dashboard/settings/components/access/AssignRolesDrawer";
import { CreateApiKeyForm } from "@/modules/dashboard/settings/components/CreateApiKeyForm";
import { DisableApiKeyDialog } from "@/modules/dashboard/settings/components/DisableApiKeyDialog";
import { EditApiKeyIpsDialog } from "@/modules/dashboard/settings/components/EditApiKeyIpsDialog";
import type {
  AllowedIp,
  ApiKeyEntity,
  CreateApiKeyResponse,
} from "@/modules/dashboard/settings/services/apiKeysService";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useOrganizationDirectory } from "@/shared/hooks/useOrganizationDirectory";
import { useTranslations } from "@/shared/lang/useTranslations";
import { ConfigService } from "@/shared/services/config.service";
import { colocaToast, toastApiError } from "@/shared/utils/toast";

import { ApiKeysCards, type ApiKeysCardsProps } from "../components/ApiKeysCards";
import { ApiKeySecretBanner } from "../components/ApiKeySecretBanner";
import { ApiKeysKpiStrip } from "../components/ApiKeysKpiStrip";
import { ApiKeysTable } from "../components/ApiKeysTable";
import { ApiKeysToolbar } from "../components/ApiKeysToolbar";
import { useApiKeysCatalog } from "../hooks/useApiKeysCatalog";

const API_DOCS_URL = new URL("/coloca/docs/public", ConfigService.getInstance().getOrThrow("colocaApiUrl"))
  .href;

const API_KEYS_CONTACT_EMAIL = "soporte@colocapayments.com";

/**
 * `/app/access/api-keys` — the API key catalog for Coloca staff.
 *
 * The backend authorizes a key by its ROLES, never by the legacy `scopes[]` it was created with, so this view
 * never presents scopes as live permissions: they appear only inside the creation form, because `POST
 * /api-keys` requires them and homologates them into base roles. After creation a key is managed purely by
 * roles — which is why it shares `AssignRolesDrawer` with `/app/users`, where the same drawer assigns the
 * same roles to a person.
 *
 * The lifecycle controls (create, enable/disable, allowed IPs) deliberately survive a roles-catalog 403: they
 * do not depend on it, and losing them because a different endpoint failed would be a worse answer than
 * showing the access column as unknown.
 */
const ApiKeysPage = () => {
  const { t } = useTranslations("settings");
  const { t: tGlobal } = useTranslations("global");
  const { isInternalUser } = useAuth();

  const catalog = useApiKeysCatalog();

  const [assignTarget, setAssignTarget] = useState<ApiKeyEntity | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [justCreated, setJustCreated] = useState<CreateApiKeyResponse | null>(null);
  const [disableTarget, setDisableTarget] = useState<ApiKeyEntity | null>(null);
  const [editIpsTarget, setEditIpsTarget] = useState<ApiKeyEntity | null>(null);

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_api_keys"), to: "/app/access/api-keys" },
      ],
      [tGlobal],
    ),
  );

  // Only INTERNAL staff see keys from more than one organization, and `GET /internal/organizations` is
  // INTERNAL-only anyway — for an org member the request is a guaranteed 403 and the column a constant.
  const directory = useOrganizationDirectory(isInternalUser);
  const { ensure: ensureOrganizations } = directory;
  const visibleOrgIds = useMemo(() => catalog.rows.map((row) => row.key.organization_id), [catalog.rows]);
  useEffect(() => {
    if (isInternalUser) ensureOrganizations(visibleOrgIds);
  }, [isInternalUser, ensureOrganizations, visibleOrgIds]);

  // One aggregated warning per load when some keys' roles could not be fetched, so an incomplete table is
  // never presented as authoritative. Keyed on the set instance — fires once per load, not per render.
  const { rolesUnavailable } = catalog;
  useEffect(() => {
    if (rolesUnavailable.size > 0) {
      colocaToast.warning(t("access_roles_partial_fail", { count: rolesUnavailable.size }));
    }
  }, [rolesUnavailable, t]);

  const reload = () => {
    catalog.reload().catch(toastApiError);
  };

  const handleCreated = async (result: CreateApiKeyResponse) => {
    setIsCreateOpen(false);
    setJustCreated(result);
    colocaToast.success(t("api_keys_create_success"));
    await catalog.reload().catch(toastApiError);
  };

  const handleEnable = async (key: ApiKeyEntity) => {
    try {
      await catalog.enable(key.id);
      colocaToast.success(t("api_keys_enable_success"));
      await catalog.reload();
    } catch (error) {
      toastApiError(error);
    }
  };

  const handleDisableConfirm = async () => {
    if (!disableTarget) return;
    try {
      await catalog.disable(disableTarget.id);
      colocaToast.success(t("api_keys_disable_success"));
      setDisableTarget(null);
      await catalog.reload();
    } catch (error) {
      toastApiError(error);
    }
  };

  const handleSaveIps = async (ips: AllowedIp[]) => {
    if (!editIpsTarget) return;
    try {
      await catalog.updateAllowedIps(editIpsTarget.id, ips);
      colocaToast.success(t("api_keys_update_success"));
      setEditIpsTarget(null);
    } catch (error) {
      toastApiError(error);
    }
  };

  const { assignedRoleIds: assignedRoleIdsOf, reloadRoles } = catalog;
  const assignedRoleIds = useMemo(
    () => (assignTarget ? assignedRoleIdsOf(assignTarget.id) : []),
    [assignTarget, assignedRoleIdsOf],
  );

  /**
   * A role's `member_count` counts users AND API keys, so assigning or revoking one here changes a number the
   * Roles view reports — and that view is not remounted when you navigate to it, because the catalog provider
   * wraps the whole sub-router. Without this it would keep showing the stale tally, and the role would stay
   * in the "sin asignar" bucket, until someone pressed Refresh. The mirror image of what `RolesPage` does
   * after a delete.
   */
  const afterRoleChange = async (change: Promise<unknown>) => {
    await change;
    reloadRoles().catch(toastApiError);
  };

  if (catalog.hasNoAccess) return <NoAccessView />;

  const empty = catalog.hasActiveFilters ? (
    <ColocaEmpty
      icon={SearchX}
      title={t("access_apikeys_empty_filtered_title")}
      description={t("access_apikeys_empty_filtered_body")}
      surface="card"
    >
      <ColocaButton variant="secondary" size="sm" onClick={catalog.clearFilters}>
        {t("access_page_clear_filters")}
      </ColocaButton>
    </ColocaEmpty>
  ) : (
    <ColocaEmpty
      icon={KeyRound}
      title={t("access_apikeys_empty_title")}
      description={t("access_apikeys_empty_body")}
      surface="card"
    >
      {catalog.canCreate && (
        <ColocaButton variant="primary" size="sm" icon={Plus} onClick={() => setIsCreateOpen(true)}>
          {t("api_keys_create_button")}
        </ColocaButton>
      )}
    </ColocaEmpty>
  );

  const footer =
    catalog.total > catalog.pageSize ? (
      <ColocaPagination
        page={catalog.page}
        perPage={catalog.pageSize}
        total={catalog.total}
        onPageChange={catalog.setPage}
        // The table's `footer` slot already draws the rule above the pagination.
        withSeparator={false}
      />
    ) : undefined;

  // Typed as the card props, which the table props extend: one object, two renderers, no chance of the
  // breakpoints diverging.
  const rowsProps: ApiKeysCardsProps = {
    rows: catalog.rows,
    loading: catalog.isLoading,
    catalogUnavailable: catalog.catalogUnavailable,
    canUpdate: catalog.canUpdate,
    canManageRoles: catalog.canManageRoles,
    showOrganization: isInternalUser,
    lookupOrganization: directory.lookup,
    isDirectoryPending: directory.isPreloading,
    isOrganizationPending: directory.isPending,
    onManageRoles: (row) => setAssignTarget(row.key),
    onEditIps: (row) => setEditIpsTarget(row.key),
    onDisable: (row) => setDisableTarget(row.key),
    onEnable: (row) => handleEnable(row.key),
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <ColocaPageHeader
        title={t("access_apikeys_title")}
        description={t("access_apikeys_sub")}
        actions={
          <>
            <ColocaButton
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              onClick={reload}
              disabled={catalog.isLoading}
            >
              {t("access_page_refresh")}
            </ColocaButton>
            <ColocaButton
              variant="default"
              size="sm"
              iconTrailing={ExternalLink}
              onClick={() => window.open(API_DOCS_URL, "_blank", "noopener,noreferrer")}
            >
              {t("api_keys_docs_button")}
            </ColocaButton>
            {catalog.canCreate && (
              <ColocaButton variant="primary" size="sm" icon={Plus} onClick={() => setIsCreateOpen(true)}>
                {t("api_keys_create_button")}
              </ColocaButton>
            )}
          </>
        }
      />

      {justCreated && <ApiKeySecretBanner created={justCreated} onDismiss={() => setJustCreated(null)} />}

      {/* `null` while loading: four zeros over a skeleton table would state that there are no keys. */}
      <ApiKeysKpiStrip
        counts={catalog.isLoading ? null : catalog.counts}
        value={catalog.bucket}
        onChange={catalog.setBucket}
        rolesPartiallyUnknown={catalog.rolesUnavailable.size > 0}
      />

      {/* The roles catalog 403'd (`roles:read` missing): effective access cannot be resolved, so it shows as
          unknown — never as an authoritative 0/N — and role management is off. The lifecycle stays. */}
      {catalog.catalogUnavailable && (
        <ColocaAlert variant="warning" icon={AlertTriangle} text={t("access_catalog_unavailable")} />
      )}

      {!catalog.canCreate && (
        <ColocaAlert
          variant="secondary"
          icon={Mail}
          heading={t("api_keys_no_write_title")}
          text={t("api_keys_no_write_description", { email: API_KEYS_CONTACT_EMAIL })}
        />
      )}

      {/* The backend holds more keys than one request can return. Disclosed, because every count and filter
          on this page describes what was loaded, and silence would present that as the whole catalog. */}
      {!catalog.isLoading && catalog.backendTotal > catalog.loadedCount && (
        <ColocaAlert
          variant="secondary"
          text={t("access_apikeys_truncated_page", {
            shown: catalog.loadedCount,
            total: catalog.backendTotal,
          })}
        />
      )}

      <ApiKeysToolbar
        query={catalog.query}
        onQueryChange={catalog.setQuery}
        roleId={catalog.roleId}
        onRoleChange={catalog.setRoleId}
        roles={catalog.roles}
        rolesUnavailable={catalog.catalogUnavailable}
      />

      <ColocaSurface padding="none" className="overflow-hidden">
        <div className="hidden lg:block">
          <ApiKeysTable {...rowsProps} empty={empty} footer={footer} />
        </div>
        <div className="lg:hidden">
          {catalog.rows.length === 0 && !catalog.isLoading ? (
            <div className="p-6">{empty}</div>
          ) : (
            <ApiKeysCards {...rowsProps} />
          )}
          {footer && <div className="border-t border-border px-4 py-3">{footer}</div>}
        </div>
      </ColocaSurface>

      <p className="text-[11.5px] text-muted-foreground">{t("access_apikeys_footnote")}</p>

      {/* A drawer, not the inline form the section used: at full-page width an inline 400-line form pushes
          the table off screen, and the secret it produces must land on the page anyway (see the banner). */}
      <ColocaDrawer
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        title={t("api_keys_create_button")}
        size="xl"
        pinLastChild={false}
      >
        <CreateApiKeyForm
          onCreate={catalog.create}
          detectCurrentIp={catalog.getCurrentIp}
          onCreated={handleCreated}
          onCancel={() => setIsCreateOpen(false)}
        />
      </ColocaDrawer>

      <AssignRolesDrawer
        principal={
          assignTarget
            ? { id: assignTarget.id, name: assignTarget.name, kind: "key", subtitle: assignTarget.key }
            : null
        }
        open={!!assignTarget}
        onOpenChange={(open) => {
          if (!open) setAssignTarget(null);
        }}
        roles={catalog.roles}
        assignedRoleIds={assignedRoleIds}
        canManage={catalog.canManageRoles}
        onAssign={(roleIds) => afterRoleChange(catalog.assignRoles(assignTarget!.id, roleIds))}
        onRemove={(roleId) => afterRoleChange(catalog.removeRole(assignTarget!.id, roleId))}
        canSplitCombinedRoles={isInternalUser}
      />

      {disableTarget && (
        <DisableApiKeyDialog
          open={!!disableTarget}
          apiKeyName={disableTarget.name}
          apiKeyPrefix={disableTarget.key}
          onConfirm={handleDisableConfirm}
          onCancel={() => setDisableTarget(null)}
        />
      )}

      {editIpsTarget && (
        <EditApiKeyIpsDialog
          open={!!editIpsTarget}
          apiKeyName={editIpsTarget.name}
          apiKeyPrefix={editIpsTarget.key}
          initialIps={editIpsTarget.allowed_ips ?? []}
          isAdmin={editIpsTarget.is_admin || editIpsTarget.super_admin}
          onSave={handleSaveIps}
          onCancel={() => setEditIpsTarget(null)}
        />
      )}
    </div>
  );
};

export default ApiKeysPage;
